// Pure Gantt model for the Projects hub. Bars come only from recorded per-phase
// schedule intervals (start AND end); there is no aggregate per-project bar.

import { T } from "../../../app/tokens.js";

const DAY = 86400000;

// Exactly seven JLL legend phases, each token-backed and distinct.
export const GANTT_LEGEND = Object.freeze([
  { key: "surveying",    label: "Surveying",           color: T.teal },
  { key: "programming",  label: "Programming",         color: T.blue },
  { key: "design",       label: "Design & Permitting", color: T.purple },
  { key: "construction", label: "Construction",        color: T.pendingAmber },
  { key: "fitup",        label: "Fit-Up",              color: T.amberDark },
  { key: "close",        label: "Close",               color: T.greenDeep },
  { key: "move",         label: "Move",                color: T.buildBlue },
]);

// User-approved display aliases only. Unknown future values stay neutral with
// recorded text. Stored enums, eligibility, sorting and dates are unchanged.
const PHASE_ALIASES = Object.freeze({
  intake: "surveying",
  business_case: "programming",
  planning: "programming",
  procurement: "design",
  move_launch: "move",
  complete: "move",
  design: "design",
  design_permitting: "design",
  build: "construction",
  construction: "construction",
  fit_up: "fitup",
  fitup: "fitup",
  closeout: "close",
  close: "close",
  move: "move",
  surveying: "surveying",
  programming: "programming",
});

export function mapGanttPhase(phase) {
  const key = typeof phase === "string" ? PHASE_ALIASES[phase.trim().toLowerCase()] : null;
  return key ? GANTT_LEGEND.find((l) => l.key === key) : null;
}

/** Strict YYYY-MM-DD -> UTC ms, or null. */
export function parseIsoDay(value) {
  if (typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(value.trim());
  if (!m) return null;
  if (value.includes("T") && !Number.isFinite(new Date(value).getTime())) return null;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  const t = Date.UTC(y, mo - 1, d);
  const chk = new Date(t);
  if (chk.getUTCFullYear() !== y || chk.getUTCMonth() !== mo - 1 || chk.getUTCDate() !== d) return null;
  return t;
}

/** Date-proportional scale padded to whole months, year-aware. */
/** points: UTC ms values that must be visible (bar endpoints, markers). */
export function buildGanttScale(points, todayMs) {
  const pts = [todayMs, ...(points || [])];
  const finite = pts.filter((p) => Number.isFinite(p));
  const lo = new Date(Math.min(...finite));
  const hi = new Date(Math.max(...finite));
  const min = Date.UTC(lo.getUTCFullYear(), lo.getUTCMonth(), 1);
  const max = Date.UTC(hi.getUTCFullYear(), hi.getUTCMonth() + 1, 1);
  const span = Math.max(max - min, DAY);
  const pct = (t) => ((t - min) / span) * 100;
  const months = [];
  const quarters = [];
  let y = lo.getUTCFullYear(); let mo = lo.getUTCMonth();
  while (Date.UTC(y, mo, 1) < max) {
    const s = Date.UTC(y, mo, 1); const e = Date.UTC(y, mo + 1, 1);
    months.push({ key: `${y}-${mo}`, label: new Date(s).toLocaleString("en-US", { month: "short", timeZone: "UTC" }), left: pct(s), width: pct(e) - pct(s), yearStart: mo === 0 });
    const q = Math.floor(mo / 3);
    const last = quarters[quarters.length - 1];
    if (last && last.y === y && last.q === q) last.width += pct(e) - pct(s);
    else quarters.push({ key: `${y}-Q${q + 1}`, y, q, label: `Q${q + 1} ${y}`, left: pct(s), width: pct(e) - pct(s) });
    mo += 1; if (mo > 11) { mo = 0; y += 1; }
  }
  return { min, max, pct, months, quarters, monthCount: months.length };
}

export function todayUtcMs(now = new Date()) {
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

// ── Phase schedule (Slice 1) ────────────────────────────────────────────────

export const SCHEDULE_PHASES = Object.freeze(GANTT_LEGEND.map((l) => l.key));

/** Ordered 7-entry list; unknown phase keys from the server are ignored. */
export function normalizePhases(schedule) {
  const byKey = new Map((schedule?.phases || []).filter((p) => p && SCHEDULE_PHASES.includes(p.phase)).map((p) => [p.phase, p]));
  return GANTT_LEGEND.map((l) => {
    const p = byKey.get(l.key) || {};
    return {
      phase: l.key, label: l.label, color: l.color,
      startDate: p.startDate ?? null, endDate: p.endDate ?? null,
      source: p.source ?? null, verified: p.verified === true,
      updatedBy: p.updatedBy ?? null, updatedAt: p.updatedAt ?? null,
    };
  });
}

/** "bar" | "partial" | "invalid" | "none" for one phase entry. */
export function phaseInterval(entry) {
  const hasS = entry?.startDate != null && entry.startDate !== "";
  const hasE = entry?.endDate != null && entry.endDate !== "";
  if (!hasS && !hasE) return { kind: "none" };
  const s = hasS ? parseIsoDay(entry.startDate) : null;
  const e = hasE ? parseIsoDay(entry.endDate) : null;
  if ((hasS && s == null) || (hasE && e == null)) return { kind: "invalid", reason: "Unreadable date" };
  if (s == null || e == null) return { kind: "partial" };
  if (e < s) return { kind: "invalid", reason: "End before start" };
  return { kind: "bar", start: s, end: e };
}

function isApprox(entry) {
  return entry.verified !== true;
}

export function buildScheduleRow(project, schedule, { portfolio = false } = {}) {
  const phases = normalizePhases(schedule);
  const bars = []; const partial = []; const invalid = [];
  for (const p of phases) {
    const iv = phaseInterval(p);
    if (iv.kind === "bar") bars.push({ key: p.phase, label: p.label, color: p.color, start: iv.start, end: iv.end, startDate: p.startDate.slice(0, 10), endDate: p.endDate.slice(0, 10), approx: isApprox(p), source: p.source });
    else if (portfolio && iv.kind === "partial" && parseIsoDay(p.startDate) != null && p.endDate == null) {
      bars.push({ key: p.phase, label: p.label, color: p.color, start: parseIsoDay(p.startDate), end: null, startDate: p.startDate.slice(0, 10), endDate: null, openEnd: true, approx: isApprox(p), source: p.source });
    }
    else if (iv.kind === "partial") partial.push(p.phase);
    else if (iv.kind === "invalid") invalid.push(p.phase);
  }
  // Markers are canonical with the schedule response when one exists.
  const fdobRaw = schedule ? (schedule.fdobDate ?? null) : (project?.fdobDate ?? null);
  const fdob = parseIsoDay(fdobRaw);
  const ms = project?.nextMilestone;
  const msDate = parseIsoDay(ms?.dueDate);
  const comm = parseIsoDay(schedule?.commencementDate);
  const markers = [];
  if (fdob != null) markers.push({ type: "fdob", t: fdob, label: `FDOB ${fdobRaw.slice(0, 10)}` });
  if (msDate != null) markers.push({ type: "milestone", t: msDate, label: `${ms.name || "Milestone"} ${ms.dueDate.slice(0, 10)}` });
  if (comm != null) markers.push({ type: "commencement", t: comm, label: `Commencement ${schedule.commencementDate.slice(0, 10)}` });
  const kind = bars.length ? "bar" : (partial.length || invalid.length) ? "partial" : "none";
  const unverified = bars.some((b) => b.approx);
  const sources = [...new Set(bars.map((b) => b.source).filter(Boolean))];
  return {
    id: project?.id, name: project?.name, phase: project?.phase ?? null, legend: mapGanttPhase(project?.phase),
    bars, partial, invalid, markers, kind, unverified, sources, hasSchedule: !!schedule,
    hasPhaseRows: Array.isArray(schedule?.phases) && schedule.phases.length > 0,
    endpoints: phases.flatMap(p => [parseIsoDay(p.startDate), parseIsoDay(p.endDate)]).filter(p => p != null),
  };
}

/** Scale covering every bar endpoint and marker plus today. */
export function buildScheduleScale(rows, todayMs) {
  const pts = [];
  for (const r of rows) {
    pts.push(...(r.endpoints || []));
    for (const b of r.bars) pts.push(b.start, b.end);
    for (const m of r.markers) pts.push(m.t);
  }
  return buildGanttScale(pts, todayMs);
}

/** Portfolio-only windows; no synthetic end date is attached to an open phase. */
export function buildPortfolioScale(rows, todayMs, range = "24") {
  if (range === "all") return buildScheduleScale(rows, todayMs);
  const half = range === "12" ? 6 : 12;
  const today = new Date(todayMs);
  const shift = (months) => {
    const first = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + months, 1));
    const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
    return Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(today.getUTCDate(), lastDay));
  };
  const min = shift(-half); const max = shift(half);
  const pct = t => (t - min) / (max - min) * 100;
  const calendar = buildGanttScale([min, max - DAY], todayMs);
  const project = cells => cells.map(cell => {
    const start = calendar.min + cell.left / 100 * (calendar.max - calendar.min);
    const end = start + cell.width / 100 * (calendar.max - calendar.min);
    return { ...cell, left: pct(Math.max(min, start)), width: pct(Math.min(max, end)) - pct(Math.max(min, start)) };
  }).filter(cell => cell.width > 0);
  return { min, max, pct, months: project(calendar.months), quarters: project(calendar.quarters), monthCount: half * 2 };
}

export function clipPortfolioBar(bar, scale) {
  const end = bar.openEnd ? scale.max : bar.end;
  if (bar.start > scale.max || end < scale.min) return null;
  return {
    left: scale.pct(Math.max(bar.start, scale.min)),
    width: scale.pct(Math.min(end, scale.max)) - scale.pct(Math.max(bar.start, scale.min)),
    clippedLeft: bar.start < scale.min,
    clippedRight: !bar.openEnd && end > scale.max,
  };
}

// ── Editor draft ────────────────────────────────────────────────────────────

const day = (v) => (typeof v === "string" && v ? v.slice(0, 10) : "");

export function draftFromSchedule(schedule) {
  const phases = {};
  for (const p of normalizePhases(schedule)) phases[p.phase] = { startDate: day(p.startDate), endDate: day(p.endDate) };
  return { phases, fdobDate: day(schedule?.fdobDate), commencementDate: day(schedule?.commencementDate) };
}

/** Returns { [field]: message } where field is "phase.startDate" | "fdobDate" etc. */
export function validateScheduleDraft(draft) {
  const errors = {};
  for (const key of SCHEDULE_PHASES) {
    const p = draft.phases[key] || {};
    const s = p.startDate ? parseIsoDay(p.startDate) : null;
    const e = p.endDate ? parseIsoDay(p.endDate) : null;
    if (p.startDate && s == null) errors[`${key}.startDate`] = "Enter a valid date (YYYY-MM-DD).";
    if (p.endDate && e == null) errors[`${key}.endDate`] = "Enter a valid date (YYYY-MM-DD).";
    if (s != null && e != null && e < s) errors[`${key}.endDate`] = "End must be on or after start.";
  }
  for (const f of ["fdobDate", "commencementDate"]) {
    if (draft[f] && parseIsoDay(draft[f]) == null) errors[f] = "Enter a valid date (YYYY-MM-DD).";
  }
  return errors;
}

/** PUT body; markers only when changed so plain phase saves never trigger FDOB reconcile. */
export function buildScheduleSaveBody(draft, original) {
  const body = {
    phases: SCHEDULE_PHASES.map((key) => ({
      phase: key,
      startDate: draft.phases[key]?.startDate || null,
      endDate: draft.phases[key]?.endDate || null,
    })),
  };
  for (const f of ["fdobDate", "commencementDate"]) {
    if ((draft[f] || "") !== (original?.[f] || "")) body[f] = draft[f] || null;
  }
  return body;
}

export function isDraftDirty(draft, original) {
  return JSON.stringify(draft) !== JSON.stringify(original);
}

/** Preview schedule object from a draft (same shape as server response). */
export function scheduleFromDraft(draft, base) {
  const prev = new Map(normalizePhases(base).map((p) => [p.phase, p]));
  return {
    projectId: base?.projectId,
    fdobDate: draft.fdobDate || null,
    commencementDate: draft.commencementDate || null,
    phases: SCHEDULE_PHASES.map((key) => ({ ...prev.get(key), phase: key, startDate: draft.phases[key]?.startDate || null, endDate: draft.phases[key]?.endDate || null })),
  };
}
